import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Card,
  ErrorPanel,
  Loading,
  PageHeader,
} from "../components/ResourceView";
import { TentAssemblyManager } from "../components/TentAssemblyManager";
import { CreateSerializedPartCard } from "../components/CreateSerializedPartCard";
import { InventoryProductManager } from "../components/InventoryProductManager";
import { useOrganization } from "../org/OrganizationProvider";
import { useRpcResource } from "../lib/useRpcResource";
import {
  inventoryApi,
  type AvailabilityRow,
  type InventoryAssetsWorkspace,
} from "../lib/inventory";

function label(v: string) {
  return v.replaceAll("_", " ");
}
function dt(v: string | null) {
  return v ? new Date(v).toLocaleString() : "—";
}
export function InventoryAssetsPage() {
  const { organizationId } = useOrganization();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [locationId, setLocationId] = useState("");
  const [tracking, setTracking] = useState("");
  const resource = useRpcResource<InventoryAssetsWorkspace>(
    async () =>
      organizationId
        ? inventoryApi.assets(
            organizationId,
            query || null,
            locationId || null,
            tracking || null,
          )
        : {
            data: null,
            error: { message: "Organization context is missing." },
          },
    [organizationId, query, locationId, tracking],
  );
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<
    "new_part" | "availability" | null
  >(null);
  const [inventoryRevision, setInventoryRevision] = useState(0);
  const [availability, setAvailability] = useState<AvailabilityRow | null>(
    null,
  );
  const [availProduct, setAvailProduct] = useState("");
  const [availLocation, setAvailLocation] = useState("");
  const [availStart, setAvailStart] = useState("");
  const [availEnd, setAvailEnd] = useState("");
  const [holdProduct, setHoldProduct] = useState("");
  const [holdLocation, setHoldLocation] = useState("");
  const [holdAsset, setHoldAsset] = useState("");
  const [holdType, setHoldType] = useState("manual");
  const [holdQty, setHoldQty] = useState("1");
  const [holdStart, setHoldStart] = useState("");
  const [holdEnd, setHoldEnd] = useState("");
  const [holdReason, setHoldReason] = useState("");
  if (resource.loading && !resource.data) return <Loading />;
  if (resource.error)
    return (
      <>
        <PageHeader title="Inventory & assets" />
        <ErrorPanel message={resource.error} />
      </>
    );
  if (!resource.data)
    return <ErrorPanel message="Inventory workspace did not return data." />;
  const data = resource.data;
  async function run(
    fn: () => Promise<{ data: unknown; error: { message: string } | null }>,
    success: string,
  ) {
    setActionError(null);
    setMessage(null);
    const r = await fn();
    if (r.error) {
      setActionError(r.error.message);
      return false;
    }
    setMessage(success);
    await resource.reload();
    return true;
  }
  async function checkAvailability() {
    if (
      !organizationId ||
      !availProduct ||
      !availLocation ||
      !availStart ||
      !availEnd
    )
      return;
    setActionError(null);
    const r = await inventoryApi.availability(
      organizationId,
      availLocation,
      availProduct,
      new Date(availStart).toISOString(),
      new Date(availEnd).toISOString(),
    );
    if (r.error) {
      setActionError(r.error.message);
      setAvailability(null);
    } else setAvailability(r.data);
  }
  async function placeHold() {
    if (!organizationId || !holdProduct || !holdLocation || !holdReason) return;
    const ok = await run(
      () =>
        inventoryApi.placeHold({
          organizationId,
          productId: holdProduct,
          locationId: holdLocation,
          assetId: holdAsset || null,
          holdType,
          quantity: Number(holdQty) || 1,
          startsAt: holdStart ? new Date(holdStart).toISOString() : null,
          endsAt: holdEnd ? new Date(holdEnd).toISOString() : null,
          reason: holdReason,
        }),
      "Supply hold created.",
    );
    if (ok) {
      setHoldReason("");
      setHoldAsset("");
    }
  }
  return (
    <>
      <PageHeader
        title="Inventory & assets"
        subtitle="Rental catalog, opening counts, availability, bulk positions, serialized assets and supply controls."
        actions={
          <>
            {(data.capabilities.manage || data.capabilities.assets_manage) && (
              <button
                className="primary"
                aria-expanded={activeTool === "new_part"}
                onClick={() =>
                  setActiveTool((value) =>
                    value === "new_part" ? null : "new_part",
                  )
                }
              >
                Create new serialized part
              </button>
            )}
            {data.capabilities.availability_view && (
              <button
                aria-expanded={activeTool === "availability"}
                onClick={() =>
                  setActiveTool((value) =>
                    value === "availability" ? null : "availability",
                  )
                }
              >
                Availability check
              </button>
            )}
            <Link className="button-link" to="/inventory/transfers">
              Transfers
            </Link>
            <Link className="button-link" to="/inventory/adjustments">
              Adjustments
            </Link>
            <button onClick={() => void resource.reload()}>Refresh</button>
          </>
        }
      />
      {(message || actionError) && (
        <div className={actionError ? "error-box" : "success-box"}>
          {actionError ?? message}
        </div>
      )}
      <div className="metric-grid six inventory-metrics">
        <div className="metric">
          <span>Serialized assets</span>
          <strong>{data.metrics.serialized_assets}</strong>
        </div>
        <div className="metric">
          <span>Available assets</span>
          <strong>{data.metrics.available_assets}</strong>
        </div>
        <div className="metric">
          <span>Bulk positions</span>
          <strong>{data.metrics.bulk_positions}</strong>
        </div>
        <div className="metric">
          <span>Opening counts required</span>
          <strong>{data.metrics.uncounted_bulk_products}</strong>
        </div>
        <div className="metric">
          <span>Active holds</span>
          <strong>{data.metrics.active_holds}</strong>
        </div>
        <div className="metric">
          <span>Active transfers</span>
          <strong>{data.metrics.active_transfers}</strong>
        </div>
        <div className="metric">
          <span>Pending adjustments</span>
          <strong>{data.metrics.pending_adjustments}</strong>
        </div>
      </div>
      <TentAssemblyManager
        organizationId={organizationId!}
        refreshKey={inventoryRevision}
      />
      <Card>
        <div className="filter-bar inventory-filter">
          <label className="field">
            <span>Location</span>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">All locations</option>
              {data.locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Tracking</span>
            <select
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
            >
              <option value="">All inventory</option>
              <option value="bulk">Bulk</option>
              <option value="serialized">Serialized</option>
            </select>
          </label>
          <label className="field inventory-search">
            <span>Search</span>
            <div className="search-row">
              <input
                value={search}
                placeholder="Product, SKU, asset or identifier"
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setQuery(search);
                }}
              />
              <button onClick={() => setQuery(search)}>Search</button>
            </div>
          </label>
        </div>
      </Card>
      {activeTool === "new_part" && (
        <CreateSerializedPartCard
          organizationId={organizationId!}
          locations={data.locations}
          onClose={() => setActiveTool(null)}
          onCreated={async () => {
            setMessage("New serialized part and asset created.");
            setActiveTool(null);
            setInventoryRevision((value) => value + 1);
            await resource.reload();
          }}
        />
      )}
      {activeTool === "availability" && (
        <section className="card inventory-availability-card">
          <div className="inventory-new-part-heading">
            <h2>Availability check</h2>
            <button type="button" onClick={() => setActiveTool(null)}>
              Close
            </button>
          </div>
          <div className="form-grid two">
            <label className="field">
              <span>Product</span>
              <select
                value={availProduct}
                onChange={(e) => setAvailProduct(e.target.value)}
              >
                <option value="">Select product</option>
                {data.products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.sku ? `${p.sku} · ` : ""}
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Location</span>
              <select
                value={availLocation}
                onChange={(e) => setAvailLocation(e.target.value)}
              >
                <option value="">Select location</option>
                {data.locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Start</span>
              <input
                type="datetime-local"
                value={availStart}
                onChange={(e) => setAvailStart(e.target.value)}
              />
            </label>
            <label className="field">
              <span>End</span>
              <input
                type="datetime-local"
                value={availEnd}
                onChange={(e) => setAvailEnd(e.target.value)}
              />
            </label>
          </div>
          <button
            disabled={
              !data.capabilities.availability_view ||
              !availProduct ||
              !availLocation ||
              !availStart ||
              !availEnd
            }
            onClick={() => void checkAvailability()}
          >
            Check availability
          </button>
          {availability && (
            <div className="availability-result">
              <strong>{label(availability.availability_state)}</strong>
              <span>
                Physical supply: {availability.physical_supply_quantity}
              </span>
              <span>
                Known demand: {availability.total_known_demand_quantity}
              </span>
              <span>Available: {availability.available_quantity}</span>
              <span>Shortage: {availability.shortage_quantity}</span>
              <span>
                Overlapping reservations:{" "}
                {availability.overlapping_reservation_count}
              </span>
              <span>
                Earliest release: {dt(availability.earliest_release_at)}
              </span>
            </div>
          )}
        </section>
      )}
      <InventoryProductManager
        organizationId={organizationId!}
        refreshKey={inventoryRevision}
        onSaved={async () => {
          setMessage("Product updated.");
          setInventoryRevision((value) => value + 1);
          await resource.reload();
        }}
      />
      {data.capabilities.manage && (
        <Card title="Supply holds">
          <div className="form-grid four">
            <label className="field">
              <span>Product</span>
              <select
                value={holdProduct}
                onChange={(e) => {
                  setHoldProduct(e.target.value);
                  setHoldAsset("");
                }}
              >
                <option value="">Select product</option>
                {data.products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Location</span>
              <select
                value={holdLocation}
                onChange={(e) => {
                  setHoldLocation(e.target.value);
                  setHoldAsset("");
                }}
              >
                <option value="">Select location</option>
                {data.locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Serialized asset (optional)</span>
              <select
                value={holdAsset}
                onChange={(e) => setHoldAsset(e.target.value)}
              >
                <option value="">Bulk / product hold</option>
                {data.assets
                  .filter(
                    (a) =>
                      a.product_id === holdProduct &&
                      a.home_location_id === holdLocation,
                  )
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.asset_number}
                    </option>
                  ))}
              </select>
            </label>
            <label className="field">
              <span>Hold type</span>
              <select
                value={holdType}
                onChange={(e) => setHoldType(e.target.value)}
              >
                <option value="manual">Manual</option>
                <option value="maintenance">Maintenance</option>
                <option value="quality">Quality</option>
                <option value="inspection">Inspection</option>
              </select>
            </label>
            <label className="field">
              <span>Quantity</span>
              <input
                type="number"
                min="0"
                step="1"
                value={holdQty}
                onChange={(e) => setHoldQty(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Starts</span>
              <input
                type="datetime-local"
                value={holdStart}
                onChange={(e) => setHoldStart(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Ends</span>
              <input
                type="datetime-local"
                value={holdEnd}
                onChange={(e) => setHoldEnd(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Reason</span>
              <input
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
              />
            </label>
          </div>
          <button
            disabled={!holdProduct || !holdLocation || !holdReason}
            onClick={() => void placeHold()}
          >
            Place hold
          </button>
          {data.holds.length > 0 && (
            <div className="stack-list inventory-hold-list inventory-scroll-list">
              {data.holds.map((h) => (
                <div className="list-row" key={h.id}>
                  <div>
                    <strong>
                      {h.product_name} · {h.location_name}
                    </strong>
                    <span>
                      {label(h.hold_type)} · Qty {h.quantity} · {h.reason}
                    </span>
                  </div>
                  <button
                    className="ghost"
                    onClick={() =>
                      void run(
                        () =>
                          inventoryApi.releaseHold(
                            h.id,
                            "Released from inventory workspace",
                          ),
                        "Supply hold released.",
                      )
                    }
                  >
                    Release
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      <section className="table-card">
        <div className="table-title">
          <div>
            <h2>Bulk catalog awaiting opening count</h2>
            <small>
              Catalog products are real Mahaffey public listings, but quantity
              remains unknown until a governed opening count is entered.
            </small>
          </div>
          <Link className="button-link" to="/inventory/adjustments">
            Enter governed counts
          </Link>
        </div>
        {data.uncounted_bulk_products.length === 0 ? (
          <div className="empty-state">
            Every matching bulk product has an inventory position.
          </div>
        ) : (
          <div className="table-wrap inventory-scroll-list">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Tracking</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.uncounted_bulk_products.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.name}</strong>
                      <small>{p.sku ?? "No internal key"}</small>
                    </td>
                    <td>{p.category ?? "—"}</td>
                    <td>{label(p.tracking_method)}</td>
                    <td>
                      <span className="status-pill status-warning">
                        Opening count required
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="table-card">
        <div className="table-title">
          <h2>Bulk inventory</h2>
        </div>
        {data.bulk_positions.length === 0 ? (
          <div className="empty-state">
            No counted bulk inventory positions match the current filters.
          </div>
        ) : (
          <div className="table-wrap inventory-scroll-list">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Location</th>
                  <th>Fleet</th>
                  <th>On hand</th>
                  <th>Last movement</th>
                </tr>
              </thead>
              <tbody>
                {data.bulk_positions.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <strong>{b.product_name}</strong>
                      <small>{b.sku ?? "No SKU"}</small>
                    </td>
                    <td>{b.location_name}</td>
                    <td>{b.fleet_quantity}</td>
                    <td>{b.on_hand_quantity}</td>
                    <td>{dt(b.last_movement_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="table-card">
        <div className="table-title">
          <h2>Serialized assets</h2>
        </div>
        {data.assets.length === 0 ? (
          <div className="empty-state">
            No serialized assets match the current filters.
          </div>
        ) : (
          <div className="table-wrap inventory-scroll-list">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Product</th>
                  <th>Location</th>
                  <th>Availability</th>
                  <th>Operational</th>
                  <th>Condition</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.assets.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <strong>{a.asset_number}</strong>
                      <small>
                        {a.primary_identifier
                          ? `${a.primary_identifier_type}: ${a.primary_identifier}`
                          : "No identifier"}
                      </small>
                    </td>
                    <td>
                      <strong>{a.product_name}</strong>
                      <small>{a.sku ?? "No SKU"}</small>
                    </td>
                    <td>{a.current_location ?? a.home_location ?? "—"}</td>
                    <td>
                      <span
                        className={`status-pill status-${a.availability_status}`}
                      >
                        {label(a.availability_status)}
                      </span>
                    </td>
                    <td>{label(a.operational_status)}</td>
                    <td>{label(a.condition_status)}</td>
                    <td>
                      <Link
                        className="button-link"
                        to={`/inventory/assets/${a.id}`}
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
